import { guid, isNullOrEmpty } from "../../../core/helpers/string-util.js";
import Model from "../../Model.js";
import TranslatedString from "../../TranslatedString.js";

/**
 * Generic report item. Report rows, columns and cells are concrete
 * subclasses.
 *
 * Most report items have a back pointer to their template counter-part: a
 * `row` has a pointer to a `rowTemplate` and a `column` a pointer to a
 * `columnTemplate`.
 *
 * Report cells and items dynamically computed at template interpretation
 * time have no template counter-part.
 *
 * @abstract
 * @param {report} spec.report - Report this item belongs to
 * @param {AbstractItemTemplate} spec.template - Item template this item has
 * been instanciated from.
 * @param {string} spec.title - Title of the item
 * @param {boolean} [spec.hidden] - True if the row should not be visible in
 * the report viewer or exporters
 */
class AbstractItem extends Model {
	constructor({ report, template, title = "", id = guid(), hidden } = {}) {
		super(...arguments);

		this._template = template;
		this._title = title;
		this._id = id;
		this._hidden = hidden;

		// This is needed to avoid a reference cycle:
		Object.defineProperty(this, "_report", {
			enumerable: false,
			value: report,
			writable: true,
		});

		// This is needed to avoid a reference cycle:
		Object.defineProperty(this, "_parent", {
			enumerable: false,
			value: report,
			writable: true,
		});
	}

	accept(visitor) {
		return visitor.visitAbstractItem(this);
	}

	getType() {
		return this.constructor.getType();
	}

	getTemplate() {
		return this._template;
	}

	getReport() {
		return this._report;
	}

	getReportId() {
		return this._report.getId();
	}

	setReport(report) {
		this._report = report;
	}

	getParent() {
		return this._parent;
	}

	setParent(parent) {
		this._parent = parent;
	}

	getId() {
		return this._id;
	}

	/**
	 * This returns an identifier for the receiver that will be the
	 * same for all other items generated from the same item template.
	 */
	getStableId() {
		return this.getTemplate().getId();
	}

	getTitle() {
		return this._title;
	}

	hasTitle() {
		return !!this._title;
	}

	getTitleString() {
		return (this.getTitle() || "").toString();
	}

	/**
	 * Return the title of the column, potentially including labels for its
	 * dimensions and/or budget.
	 */
	getFullTitleString() {
		let title = this.getTitleString();

		if (this.hasBudget()) {
			title = `${this.getBudgetName()} ${title}`;
		}
		if (this.hasOwnDimensionSpecification()) {
			title = `${title} - ${this.getShortDimensionText()}`;
		}

		return title;
	}

	getHidden() {
		return this._hidden;
	}

	setHidden(hidden) {
		this._hidden = hidden;
	}

	setVisible(state) {
		this._hidden = !state;
	}

	isOptional() {
		return this._template?.isOptional();
	}

	/**
	 * @deprecated - Please use the report directly
	 */
	isVisible() {
		return this._report.isItemVisible(this);
	}

	getDimensionTree() {
		return undefined;
	}

	/**
	 * Return a formula node built from the report item.
	 */
	asFormulaNode() {
		return this._asBaseFormulaNode();
	}

	validate() {
		super.validate(...arguments);

		if (this._template) {
			this._template.validate(...arguments);
		}
	}

	isRow() {
		return false;
	}

	isColumn() {
		return false;
	}

	hasSettings() {
		return false;
	}

	hasOwnDimensionSpecification() {
		return false;
	}

	hasBudget() {
		return false;
	}

	/**
	 * @return {boolean} - Whether the receiver can be part of a
	 * formula.
	 */
	canBeUsedInFormulas() {
		return this.canBeReferenced();
	}

	/**
	 * @return {boolean} - Whether another row/column can reference the
	 * receiver for its own purpose: e.g., defining visibility or
	 * defining the base row of a percentage column.
	 */
	canBeReferenced() {
		return true;
	}

	canBeSelected() {
		return true;
	}

	canBeCalculated() {
		return true;
	}

	_asBaseFormulaNode() {
		this._subclassResponsibility();
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.id = this._id;

		properties.title = isNullOrEmpty(this._title)
			? undefined
			: this._title.toString();

		properties.hidden = this._hidden;

		properties.templateId = this._template && this._template.getId();

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.template =
			data.templateId && context.template.getItemById(data.templateId);

		data.title &&= TranslatedString.materialize(data.title, context);
	}
}

export default AbstractItem;
